import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationsApi } from '../../services/api';
import { useOrganizationStore } from '../../stores/organizationStore';
import { queryKeys } from '../../lib/queryClient';
import { Organization } from '../../types/api';

/**
 * Hook to fetch current organization
 */
export const useCurrentOrganization = () => {
  const { setOrganization, setLoading, setError } = useOrganizationStore();
  
  const query = useQuery({
    queryKey: queryKeys.organizationCurrent(),
    queryFn: organizationsApi.getCurrentOrganization,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Handle side effects
  React.useEffect(() => {
    if (query.data) {
      setOrganization(query.data);
      setLoading(false);
    }
    if (query.error) {
      setError((query.error as any).response?.data?.detail || 'Failed to fetch organization');
      setLoading(false);
    }
  }, [query.data, query.error, setOrganization, setLoading, setError]);

  return query;
};

/**
 * Hook to update current organization
 */
export const useUpdateCurrentOrganization = () => {
  const queryClient = useQueryClient();
  const { setOrganization } = useOrganizationStore();
  
  return useMutation({
    mutationFn: (data: Partial<Organization>) => 
      organizationsApi.updateCurrentOrganization(data),
    onSuccess: (data) => {
      // Update store
      setOrganization(data);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: queryKeys.organizationCurrent() });
    },
  });
};

/**
 * Hook to fetch organization statistics
 */
export const useOrganizationStats = () => {
  const { updateStats } = useOrganizationStore();
  
  const query = useQuery({
    queryKey: queryKeys.organizationStats(),
    queryFn: organizationsApi.getOrganizationStats,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  React.useEffect(() => {
    if (query.data) {
      updateStats(query.data);
    }
  }, [query.data, updateStats]);

  return query;
};

/**
 * Hook to fetch organization features
 */
export const useOrganizationFeatures = () => {
  const { updateFeatures } = useOrganizationStore();
  
  const query = useQuery({
    queryKey: queryKeys.organizationFeatures(),
    queryFn: organizationsApi.getOrganizationFeatures,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  React.useEffect(() => {
    if (query.data) {
      updateFeatures(query.data);
    }
  }, [query.data, updateFeatures]);

  return query;
};

/**
 * Hook to fetch organization limits
 */
export const useOrganizationLimits = () => {
  const { updateLimits } = useOrganizationStore();
  
  const query = useQuery({
    queryKey: queryKeys.organizationLimits(),
    queryFn: organizationsApi.getOrganizationLimits,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  React.useEffect(() => {
    if (query.data) {
      updateLimits(query.data);
    }
  }, [query.data, updateLimits]);

  return query;
};

/**
 * Hook to fetch all organizations (super admin only)
 */
export const useAllOrganizations = () => {
  return useQuery({
    queryKey: queryKeys.organizations(),
    queryFn: organizationsApi.getAllOrganizations,
  });
};
